import { useCallback, useMemo } from 'react'

import { useController, useFormContext } from 'react-hook-form'
import TimePicker from 'react-time-picker'

import { classNames } from '@/utils/helpers'

import {
  constructDate,
  getFormattedTime,
  getIsForbidAM,
  getIsMinTime,
  getIsPM,
} from '../../utils'
import { AmPmSwitch } from '../AmPmSwitch/AmPmSwitch'
import './calendar-time-picker.css'

interface CalendarTimePickerProps<T = Date> {
  className?: string
  minTime?: string
  name: string
  fromDate?: Date
  defaultDate?: Date
  convertToDate?: (value: T) => Date
  convertFromDate?: (date: Date) => T
}

function isDateType(value: unknown): value is Date {
  return Object.prototype.toString.call(value) === '[object Date]'
}

// eslint-disable-next-line react/function-component-definition
export function CalendarTimePicker<T>(props: CalendarTimePickerProps<T>) {
  const {
    className,
    minTime,
    name,
    fromDate,
    defaultDate,
    convertFromDate,
    convertToDate,
  } = props
  const { control } = useFormContext()
  const { field } = useController({ name, control })

  const date = useMemo(
    () => {
      if (convertToDate) {
        return convertToDate(field.value ?? defaultDate)
      }
      if (!isDateType(field.value)) {
        throw new Error('rawDate must be a Date')
      }

      return field.value
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [convertToDate, field.value],
  )

  const time = getFormattedTime(date)
  const isMinTime = getIsMinTime(date, fromDate)
  const isForbidAM = getIsForbidAM(date, fromDate)
  const isPM = isForbidAM || getIsPM(date)
  const _minTime = isMinTime ? minTime : undefined

  const handleToggle = useCallback(() => {
    if (isForbidAM && isPM) {
      return
    }
    const timedDate = constructDate(date, time, !isPM)
    const value = convertFromDate ? convertFromDate(timedDate) : timedDate

    field.onChange(value)
  }, [convertFromDate, date, field, isForbidAM, isPM, time])

  const onTimeChange = useCallback(
    (newTime: string | null) => {
      if (!newTime) {
        return
      }
      const timedDate = constructDate(date, newTime, isPM)
      const value = convertFromDate ? convertFromDate(timedDate) : timedDate

      field.onChange(value)
    },
    [convertFromDate, date, field, isPM],
  )

  return (
    <div className={classNames('flex items-center gap-2', className)}>
      <TimePicker
        amPmAriaLabel="Select AM/PM"
        className="h-full rounded-md text-title placeholder:text-title dark:bg-secondary-700"
        maxDetail="second"
        onChange={onTimeChange}
        format="hh:mm a"
        value={time}
        clearIcon={null}
        clockIcon={null}
        disableClock
        minTime={_minTime}
      />
      <AmPmSwitch
        onColor="#fff"
        isOn={isPM}
        handleToggle={handleToggle}
      />
    </div>
  )
}
